import type { ReactNode } from 'react';
import { PencilIcon } from '@heroicons/react/24/outline';
import { Tooltip, type DataTableColumn } from '@odo/shared-ui';
import { CategoryChip } from '../CategoryChip';
import { formatMoneyDollars } from '../../../utils/money';
import { STATUS_LABEL, type DiscountsSortBy, type RuleRow, type RuleStatus } from './discountRows';

// Plain functions, not components: a "*Columns.tsx" file's only component
// export is column-preset render output (see practiceColumns.tsx's convention).

function fmtDate(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}

function scheduleText(row: RuleRow): string {
    const { startsAt, endsAt } = row.rule;
    if (startsAt && endsAt) return `${fmtDate(startsAt)} → ${fmtDate(endsAt)}`;
    if (startsAt) return `from ${fmtDate(startsAt)}`;
    if (endsAt) return `until ${fmtDate(endsAt)}`;
    return 'Always';
}

function rateLabel(row: RuleRow): string {
    return row.rule.discountType === 'percentage'
        ? `-${row.rule.discountValue}%`
        : `-${formatMoneyDollars(row.rule.discountValue)}`;
}

const STATUS_TONE: Record<RuleStatus, { cls: string; strike?: boolean }> = {
    active: { cls: 'bg-green-50 text-green-600' },
    scheduled: { cls: 'bg-blue-50 text-blue-600' },
    ended: { cls: 'bg-gray-100 text-gray-500', strike: true }
};

function ruleStatusBadge(status: RuleStatus): ReactNode {
    const tone = STATUS_TONE[status];
    return (
        <span
            className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${tone.cls}`}
        >
            <span className="w-1 h-1 rounded-full bg-current opacity-80" />
            <span className={tone.strike ? 'line-through' : ''}>{STATUS_LABEL[status]}</span>
        </span>
    );
}

function eligibleCell(eligible: number, total: number): ReactNode {
    const pct = total > 0 ? Math.round((eligible / total) * 100) : 0;
    return (
        <div className="flex flex-col gap-1 w-[120px]">
            <div className="flex items-baseline justify-between">
                <span className="text-[13px] font-medium text-gray-700 tabular-nums">
                    {eligible}
                </span>
                <span className="text-[11px] text-gray-400 tabular-nums">{pct}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full rounded-full bg-gray-700" style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}

function versionLabel(row: RuleRow): string | null {
    if (!row.rule.appliesToVersionId) return null;
    return (
        row.product.versions.find((v) => v.id === row.rule.appliesToVersionId)?.label ??
        'One version'
    );
}

interface BuildDiscountColumnsArgs {
    totalPractices: number;
    canEdit: boolean;
    onEdit: (row: RuleRow) => void;
}

export function buildDiscountColumns({
    totalPractices,
    canEdit,
    onEdit
}: BuildDiscountColumnsArgs): DataTableColumn<RuleRow, DiscountsSortBy>[] {
    const columns: DataTableColumn<RuleRow, DiscountsSortBy>[] = [
        {
            key: 'rule',
            label: 'Rule',
            width: 'auto',
            sortable: true,
            sortKey: 'rule',
            cellClassName: 'pl-6 pr-3',
            headerClassName: 'whitespace-nowrap px-3 pl-6',
            // Reveals sit on the two text spans, NOT on the column's
            // `getTitle`: CategoryChip carries its own Tooltip, which would
            // open alongside a cell-level one when hovering the chip.
            render: (row) => (
                <div className="flex flex-col gap-0.5 min-w-0">
                    <Tooltip content={row.rule.name}>
                        <span className="text-sm font-medium text-gray-800 truncate">
                            {row.rule.name}
                        </span>
                    </Tooltip>
                    <span className="flex items-center gap-1.5 text-xs text-gray-400 min-w-0">
                        <Tooltip content={row.product.name}>
                            <span className="truncate">{row.product.name}</span>
                        </Tooltip>
                        <CategoryChip category={row.product.category} />
                    </span>
                </div>
            )
        },
        {
            key: 'rate',
            label: 'Rate',
            width: 'auto',
            sortable: true,
            sortKey: 'rate',
            cellClassName: 'px-3 whitespace-nowrap',
            headerClassName: 'whitespace-nowrap px-3',
            render: (row) => (
                <span className="inline-flex text-[12px] font-bold px-2 py-0.5 rounded-md bg-green-50 text-green-600 tabular-nums">
                    {rateLabel(row)}
                </span>
            )
        },
        {
            key: 'applies',
            label: 'Applies to',
            width: 'auto',
            sortable: true,
            sortKey: 'applies',
            cellClassName: 'px-3 whitespace-nowrap',
            headerClassName: 'whitespace-nowrap px-3',
            render: (row) => {
                const label = versionLabel(row);
                return label ? (
                    <span className="inline-flex text-[11px] font-medium px-2 py-0.5 rounded-md bg-gray-100 text-gray-600">
                        {label}
                    </span>
                ) : (
                    <span className="text-[13px] text-gray-400">All versions</span>
                );
            }
        },
        {
            key: 'schedule',
            label: 'Schedule',
            width: 'auto',
            sortable: true,
            sortKey: 'schedule',
            cellClassName: 'px-3 whitespace-nowrap',
            headerClassName: 'whitespace-nowrap px-3',
            render: (row) =>
                row.endingDays !== null ? (
                    <span className="text-[13px] font-medium text-yellow-700">
                        ends in {row.endingDays} day{row.endingDays === 1 ? '' : 's'}
                    </span>
                ) : (
                    <span
                        className={`text-[13px] ${scheduleText(row) === 'Always' ? 'text-gray-400' : 'text-gray-600'}`}
                    >
                        {scheduleText(row)}
                    </span>
                )
        },
        {
            key: 'status',
            label: 'Status',
            width: 'auto',
            sortable: true,
            sortKey: 'status',
            cellClassName: 'px-3 whitespace-nowrap',
            headerClassName: 'whitespace-nowrap px-3',
            render: (row) => ruleStatusBadge(row.status)
        },
        {
            key: 'eligible',
            label: 'Eligible',
            width: 'auto',
            sortable: true,
            sortKey: 'eligible',
            cellClassName: 'px-3',
            headerClassName: 'whitespace-nowrap px-3',
            render: (row) => eligibleCell(row.rule.eligibleCount, totalPractices)
        }
    ];

    if (canEdit) {
        columns.push({
            key: 'menu',
            label: '',
            width: 'auto',
            stopClickPropagation: true,
            cellClassName: 'px-3 w-12',
            headerClassName: 'whitespace-nowrap px-3',
            render: (row) => (
                <button
                    type="button"
                    onClick={() => onEdit(row)}
                    className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors"
                    aria-label="Edit rule"
                >
                    <PencilIcon className="h-4 w-4" />
                </button>
            )
        });
    }

    return columns;
}
